import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { AuthProvider } from './auth/AuthProvider';
import { ConfirmProvider } from './components/ConfirmDialog';
import { ToastProvider } from './components/Toast';
import { initI18n } from './i18n';
import { LayoutProvider } from './theme/LayoutProvider';

// Import order matters and mirrors the Razor <head>: the SmartAdmin sheet first,
// then this application's own additions, so equal-specificity rules resolve the
// way they did when the latter were inline <style> blocks.
import './styles/smartapp.scss';
import './styles/shell.scss';

/**
 * Entry point.
 *
 * i18next is awaited before the first render on purpose. `useSuspense` is off,
 * so rendering early would paint raw resource keys for a frame and then swap them
 * for translations — visible, and worse on a slow connection than a moment of
 * blank page.
 *
 * Provider order is a dependency chain, not a preference: AuthProvider issues
 * queries, so QueryClientProvider is above it; LayoutProvider writes the html
 * element and needs nothing, so it can sit anywhere above the shell.
 * ConfirmProvider and ToastProvider are innermost because they are the two here
 * that render anything — both must sit under everything their content may reach
 * for.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The app was full page loads until now; a refetch on every window focus
      // would be a behaviour change nobody asked for.
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

async function bootstrap() {
  await initI18n();

  const container = document.getElementById('root');
  if (!container) throw new Error('#root is missing from index.html.');

  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <LayoutProvider>
          <AuthProvider>
            <ConfirmProvider>
              <ToastProvider>
                <App />
              </ToastProvider>
            </ConfirmProvider>
          </AuthProvider>
        </LayoutProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
